import React, { useEffect, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
// material
import { Box, Button, Grid, IconButton } from '@mui/material';

import { CustomCheckBox, CustomFileUpload, CustomSelect, CustomTextArea, CustomTextBox } from 'components/forms';
import { CustomDrawer, HoverLoader } from 'components';
import { handleAxiosError, validationRule } from 'utils/global';
import { useToastMessage } from 'hooks';
import { services } from 'redux/services';
import { FileAcceptData, fileValidate, bannerPlatfromOptions } from 'utils/constants';
import { Add, Delete } from '@mui/icons-material';

interface Props {
    open: boolean;
    closeModal: () => void;
    formMode: 'create' | 'update';
    selectedData: any;
    headerText: string;
    refreshData: () => void;
}

const buttonInitialState = {
    name: '',
    url: '',
    hexcode: ''
}

const Form = (props: Props) => {
    const { closeModal, open, formMode, selectedData, headerText, refreshData } = props;
    const { toastMessage } = useToastMessage();
    const { handleSubmit, formState: { errors }, control, setValue, register, clearErrors,setError, reset } = useForm<any>({
        mode: 'all',
        defaultValues: { status: 1, platform:bannerPlatfromOptions[0], buttons: [buttonInitialState] }
    });
    const { fields, append, remove } = useFieldArray({
        control,
        name: "buttons",
    });

    const { formatMessage } = useIntl();
    const [loading, setLoading] = useState(false);
    const [fileImage, setFileImage] = useState({
        image: '',
    })

    function onSubmit(data: any) {
        setLoading(true);
        data.platform = data.platform?.value;
        data.image = data?.image?.[0];
        data.status = data?.status ? 1 : 0;
        if (formMode === 'update') data.id = selectedData?.id;
        const apiFunc = formMode === 'create' ? 'createBanner' : 'updateBanner';
        const message = formMode === 'create' ? 'created-message' : 'updated-message';
        services[apiFunc](data)
            .then((res: any) => {
                setLoading(false);
                toastMessage(formatMessage({ id: message }, { title: "banner" }));
                closeModal();
                refreshData();
            })
            .catch((error: any) => {
                setLoading(false);
                handleAxiosError(error, toastMessage, formatMessage, setError);
            })
    }

    //RESET DEFAULTVALUE IN UPDATE MODE
    useEffect(() => {
        if (selectedData) {
            reset({
                platform: bannerPlatfromOptions.find(x => x.value === selectedData.platform),
                title: selectedData.title,
                sub_title: selectedData.sub_title,
                description: selectedData.description,
                buttons: selectedData.buttons ?? [buttonInitialState],
                status: selectedData.status,
                order: selectedData.order,
                image: null,
            });
            setFileImage({ image: selectedData.image })
        }

    }, [formMode === 'update',selectedData]);

    return (
        <CustomDrawer
            formMode={formMode}
            open={open}
            onCancel={closeModal}
            onSubmit={handleSubmit(onSubmit)}
            title={headerText}
            size='md'
        >
            <Grid container spacing={2}>
                {loading && <HoverLoader />}
                <Grid item xs={12}>
                    <CustomSelect
                        options={bannerPlatfromOptions}
                        label={formatMessage({ id: "platform" })}
                        name="platform"
                        control={control}
                        error={errors.platform}
                        rules={{ required: true }}
                    />
                </Grid>
                <Grid item xs={12}>
                    <CustomTextBox
                        label={formatMessage({ id: "title" })}
                        name="title"
                        control={control}
                        error={errors?.title}
                        rules={validationRule.textbox({ required: true })}
                    />
                </Grid>
                {/* <Grid item xs={12}>
                    <CustomTextBox
                        label={formatMessage({ id: "sub-title" })}
                        name="sub_title"
                        control={control}
                        error={errors?.sub_title}
                        rules={validationRule.textbox({ required: true })}
                    />
                </Grid> */}
                <Grid item xs={12}>
                    <CustomTextArea
                        formLabel={formatMessage({ id: "description" })}
                        name="description"
                        control={control}
                        minRows={6}
                    />
                </Grid>
                <Grid item xs={12}>
                    <CustomFileUpload
                        accept={FileAcceptData.IMAGES}
                        label={formatMessage({ id: "image" })}
                        error={errors?.image}
                        {...register('image', {
                            validate: (value: any) => value?.length > 0 ? fileValidate(value?.[0]) : true,
                        })}
                        defaultFileUrl={fileImage.image}
                        onClear={() => {
                            setValue(`image`, null);
                            clearErrors(`image`);
                        }}
                        onChange={(file: any) => setValue('image', file)}
                    />
                </Grid>
                <Grid item xs={12}>
                    {fields.map((field, index) => (
                        <Box key={field.id} sx={{ display: 'flex', gap: 1, marginY: 1 }}>
                            <CustomTextBox
                                label={formatMessage({ id: "name" })}
                                name={`buttons.${index}.name`}
                                control={control}
                            />
                            <CustomTextBox
                                label={formatMessage({ id: "url" })}
                                name={`buttons.${index}.url`}
                                control={control}
                            />
                            <CustomTextBox
                                label={formatMessage({ id: "hexcode" })}
                                name={`buttons.${index}.hexcode`}
                                control={control}
                            />
                            <IconButton onClick={() => remove(index)}>
                                <Delete color='error' />
                            </IconButton>
                        </Box>
                    ))}
                    <Button
                        startIcon={<Add />}
                        variant='contained'
                        onClick={() => append(buttonInitialState)}
                    >
                        Add Banner Buttons
                    </Button>
                </Grid>

                <Grid item xs={12}>
                    <CustomTextBox
                        type='number'
                        label={formatMessage({ id: "order" })}
                        name="order"
                        control={control}
                    />
                </Grid>
                <Grid item xs={12}>
                    <CustomCheckBox
                        name="status"
                        label={formatMessage({ id: "status" })}
                        control={control}
                        isDefaultChecked
                    />
                </Grid>
            </Grid>
        </CustomDrawer>
    )
}

export default Form;